import * as React from "react";

import type { ReportStrategyPolicyVersion } from "../../types/reports";
import type { StrategyActivePineSummary } from "../../types/reportStrategyPine";
import {
  activePineProvenanceParts,
  formatActivePineProvenance,
  resolveActiveComposedPolicyName,
} from "../../utils/reportStrategyPineUi";

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

function ComposeModeTooltipLabel({
  label,
  description,
}: {
  label: string;
  description: string;
}): React.ReactElement {
  if (!description.trim()) {
    return <span className="font-normal text-gray-700 dark:text-gray-300">{label}</span>;
  }

  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className="cursor-help font-normal text-gray-700 underline decoration-dotted decoration-gray-400 underline-offset-2 dark:text-gray-300"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
          >
            {label}
          </span>
        </TooltipTrigger>
        <TooltipContent side="top" sideOffset={6} className="max-w-xs text-left leading-snug">
          {description}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export type ActivePineProvenanceTextProps = {
  active?: StrategyActivePineSummary | null;
  versions?: ReportStrategyPolicyVersion[];
  /** Card subtitle: "Composed from policy: …". Inline: policy name only. Legacy: compose mode line. */
  format?: "composedPolicyLine" | "policyNameOnly" | "legacy";
  className?: string;
};

export function ActivePineProvenanceText({
  active,
  versions,
  format = "composedPolicyLine",
  className,
}: ActivePineProvenanceTextProps): React.ReactElement | null {
  const policyName = resolveActiveComposedPolicyName(active, versions);

  if (format !== "legacy" && policyName) {
    if (format === "policyNameOnly") {
      return (
        <span className={className}>
          <span className="font-normal text-gray-700 dark:text-gray-300">{policyName}</span>
        </span>
      );
    }

    return (
      <span className={className}>
        <span className="font-normal text-gray-500 dark:text-white">Composed from policy: </span>
        <span className="font-normal text-gray-700 dark:text-gray-300">{policyName}</span>
      </span>
    );
  }

  const parts = activePineProvenanceParts(active);
  if (parts) {
    return (
      <span className={className}>
        <span className="font-normal text-gray-500 dark:text-white">Compose Mode: </span>
        <ComposeModeTooltipLabel
          label={parts.composeModeLabel}
          description={parts.composeModeDescription}
        />
        {parts.tail ? (
          <span className="font-normal text-gray-500 dark:text-white">
            {" · "}
            {parts.tail}
          </span>
        ) : null}
      </span>
    );
  }

  const fallback = formatActivePineProvenance(active);
  if (!fallback) return null;
  return <span className={className}>{fallback}</span>;
}
